import { Divider, Group, Stack, Text } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useAtomValue } from "jotai";

import { authProfileAtom } from "~/atoms";
import { useRenderedPathname } from "~/hooks/useRenderedPathname";
import { type NavEntry, navPageFor } from "~/utils/navManifest";

import { DEFAULT_SPACING, DEFAULT_SPACING_MOBILE } from "../../utils/Constants";
import { Org } from "../../utils/Types";
import RoundIconButton from "../Button/RoundIconButton";
import SvgIcon from "../SvgIcon";
import LeftSidebarItem from "./LeftSidebarMobileItem";
import Logo from "./Logo";
import SwitchRole from "./SwitchRole";
import { SIDEBAR_WIDTH } from "./index";

const Sidebar = ({
  currentOrg,
  navEntries,
  closeSidebar,
}: {
  currentOrg: Org;
  navEntries: NavEntry[];
  closeSidebar: () => void;
}) => {
  const navigate = useNavigate();
  const pathname = useRenderedPathname();
  const authProfile = useAtomValue(authProfileAtom);
  const currentNavPage = navPageFor(pathname);

  return (
    <Stack
      w={SIDEBAR_WIDTH * 3}
      h="100%"
      gap={0}
      style={{
        borderRight: "1px solid var(--mantine-color-border-secondary)",
      }}
    >
      <Group
        gap={4}
        wrap="nowrap"
        m={DEFAULT_SPACING_MOBILE * 8}
        w={SIDEBAR_WIDTH * 3 - DEFAULT_SPACING * 8 * 2}
      >
        <Stack gap={0}>
          <Logo size={24} />
        </Stack>
        <Text size="xs" fw={600}>
          {currentOrg?.displayName}
        </Text>
      </Group>
      <Stack justify="space-between" flex={1} gap={0}>
        <Stack gap={0} py={8}>
          {navEntries.map((entry) => (
            <LeftSidebarItem
              key={entry.id}
              entry={entry}
              isActive={currentNavPage === entry.to}
              navigate={navigate}
              closeSidebar={closeSidebar}
            />
          ))}
        </Stack>
        <Stack gap={0}>
          <Divider />
          <Group
            justify="space-between"
            gap={8}
            wrap="nowrap"
            m={DEFAULT_SPACING * 8}
          >
            <Stack gap={0}>
              <Text size="xs">{authProfile?.displayName ?? ""}</Text>
              <SwitchRole />
            </Stack>
            <RoundIconButton onClick={() => navigate({ to: "/logout" })}>
              <SvgIcon name="Logout" />
            </RoundIconButton>
          </Group>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Sidebar;
